import { useState, useCallback, useMemo } from 'react';
import { useQuery, ApolloError, useApolloClient, InMemoryCache } from '@apollo/client';
import { AttendeeConnections } from '@state/index';
import { useVAHSubscription } from '@hooks/useVAHSubscription';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import { AttendeePendingConnectionAction } from '@graphql/gql/graphql';
import { removeAttendeeFromPendingConnections } from '../cache';

export const LOG = LoggerFactory.create('usePendingConnectionCount');
const subscriptionFallback = (): void => {
  // do nothing
};

type UsePendingConnectionCountI = {
  attendeeId: string;
  eventId: string;
  skip?: boolean;
  realtimeUpdates?: boolean;
  onError?: (error: ApolloError) => void;
};

type UsePendingConnectionCountReturn = {
  pendingConnectionCount: number;
  loading: boolean;
  error?: ApolloError;
};

const usePendingConnectionCount = ({
  attendeeId,
  eventId,
  skip = false,
  realtimeUpdates = true
}: UsePendingConnectionCountI): UsePendingConnectionCountReturn => {
  const [pendingConnectionCount, setPendingConnectionCount] = useState(0);
  const skipSubscription = skip || !realtimeUpdates;
  const { cache: apolloCache } = useApolloClient();

  /**
   * Initial seeding of data will come from query
   */
  const { loading, error } = useQuery(AttendeeConnections.Queries.GET_PENDING_ATTENDEE_CONNECTION_COUNT, {
    onCompleted(_data) {
      setPendingConnectionCount(_data.getPendingAttendeeConnectionCount.pendingCount);
    },
    onError(e) {
      // This hook is non-critical
      LOG.warn(`failed to fetch initial pending connection count attendeeId ${attendeeId} eventId ${eventId}`, e);
    },
    skip
  });

  const subscriptionUpdateReceived = useCallback(
    (_data): void => {
      // if a pending connection request is cancelled, remove the pending connection from cache
      if (_data.attendeeConnectionsStatusChanged.action === AttendeePendingConnectionAction.Cancel) {
        removeAttendeeFromPendingConnections(
          _data.attendeeConnectionsStatusChanged.initiatorAttendeeId,
          apolloCache as InMemoryCache
        );
      }
      setPendingConnectionCount(_data.attendeeConnectionsStatusChanged.pendingConnectionsCount);
    },
    [setPendingConnectionCount]
  );

  /**
   * Realtime updates will come from the subscription
   */
  useVAHSubscription(
    useMemo(() => {
      return {
        variables: { eventId, attendeeId },
        query: AttendeeConnections.Subscriptions.ON_UPDATE_PENDING_ATTENDEE_CONNECTION_COUNT,
        fetchPolicy: 'no-cache'
      };
    }, [eventId, attendeeId]),
    skipSubscription,
    true,
    subscriptionUpdateReceived as () => void,
    subscriptionFallback
  );
  return { pendingConnectionCount, loading, error };
};

export default usePendingConnectionCount;
